import { PaginationMeta } from './api-response.model';

export const USER_ROL_SUPERADMIN = 1;
export const USER_ROL_COMERCIAL = 2;
export const USER_ROL_USUARIO = 3;

export const PERMISO_SECCION_CLIENTES = 1;
export const PERMISO_SECCION_CONTACTOS = 2;
export const PERMISO_SECCION_ACCIONES = 3;
export const PERMISO_SECCION_USUARIOS = 4;

export type PermisoSeccion =
  | typeof PERMISO_SECCION_CLIENTES
  | typeof PERMISO_SECCION_CONTACTOS
  | typeof PERMISO_SECCION_ACCIONES
  | typeof PERMISO_SECCION_USUARIOS;

export interface User {
  id: number;
  name: string;
  email: string;
  rol: number;
  rol_label?: string | null;
  activo: boolean;
  deleted: boolean;
  avatar_url?: string | null;
  permisos?: UserPermiso[];
}

export interface LoginData {
  token_type: string;
  access_token: string;
  expires_at: string | null;
  user: User;
}

export interface ComercialUser {
  id: number;
  name: string;
  email: string;
  rol: number;
  activo: boolean;
  deleted: boolean;
}

export interface UserPermiso {
  id: number;
  seccion: number;
  seccion_label: string;
  view: boolean;
  edit: boolean;
  delete: boolean;
}

export interface UserPermisoInput {
  seccion: number;
  seccion_label: string;
  view: boolean;
  edit: boolean;
  delete: boolean;
}

export interface UserPermisosPayload {
  permisos: Array<{
    seccion: number;
    view: boolean;
    edit: boolean;
    delete: boolean;
  }>;
}

export interface UserSeccionOption {
  seccion: number;
  label: string;
}

export const USER_SECCION_OPTIONS: UserSeccionOption[] = [
  { seccion: 1, label: 'Clients' },
  { seccion: 2, label: 'Contactes' },
  { seccion: 3, label: 'Accions comercials' },
  { seccion: 4, label: 'Usuaris' },
];

export interface UserListItem {
  id: number;
  name: string;
  email: string;
  rol: number;
  rol_label: string;
  activo: boolean;
  deleted: boolean;
  avatar_url?: string | null;
  permisos_summary: string;
  created_at: string | null;
  updated_at: string | null;
}

export interface UserDetail {
  id: number;
  name: string;
  email: string;
  rol: number;
  rol_label: string;
  activo: boolean;
  deleted: boolean;
  avatar_url?: string | null;
  created_at: string | null;
  updated_at: string | null;
  permisos: UserPermiso[];
}

export type UsersDeletedFilter = 'active' | 'deleted';

export type UsersActivoFilter = 'all' | 'active' | 'inactive';

export interface UsersListParams {
  page?: number;
  perPage?: number;
  q?: string;
  rol?: number | null;
  activoFilter?: UsersActivoFilter;
  deletedFilter?: UsersDeletedFilter;
}

export interface UsersListResult {
  items: UserListItem[];
  pagination: PaginationMeta;
}

export interface RolOption {
  label: string;
  value: number;
}

export const USER_ROL_OPTIONS: RolOption[] = [
  { label: 'Superadmin', value: USER_ROL_SUPERADMIN },
  { label: 'Comercial', value: USER_ROL_COMERCIAL },
  { label: 'Usuari', value: USER_ROL_USUARIO },
];

export interface ActivoFilterOption {
  label: string;
  value: UsersActivoFilter;
}

export const USER_ACTIVO_FILTER_OPTIONS: ActivoFilterOption[] = [
  { label: 'Tots', value: 'all' },
  { label: 'Actius', value: 'active' },
  { label: 'Inactius', value: 'inactive' },
];

export interface UserFormPayload {
  name: string;
  email: string;
  rol: number;
  activo: boolean;
  password: string;
}

export interface UserCreatePayload {
  name: string;
  email: string;
  rol: number;
  activo: boolean;
  password: string;
}

export interface UserUpdatePayload {
  name?: string;
  email?: string;
  rol?: number;
  activo?: boolean;
  password?: string;
  deleted?: boolean;
}

export function emptyUserForm(): UserFormPayload {
  return {
    name: '',
    email: '',
    rol: 2,
    activo: true,
    password: '',
  };
}

export function userToForm(user: UserDetail): UserFormPayload {
  return {
    name: user.name,
    email: user.email,
    rol: user.rol,
    activo: user.activo,
    password: '',
  };
}

export function userFormToCreatePayload(form: UserFormPayload): UserCreatePayload {
  return {
    name: form.name.trim(),
    email: form.email.trim(),
    rol: form.rol,
    activo: form.activo,
    password: form.password,
  };
}

export function userFormToUpdatePayload(form: UserFormPayload): UserUpdatePayload {
  const payload: UserUpdatePayload = {
    name: form.name.trim(),
    email: form.email.trim(),
    rol: form.rol,
    activo: form.activo,
  };

  const password = form.password.trim();

  if (password !== '') {
    payload.password = password;
  }

  return payload;
}

export function buildPermisosFormState(user: UserDetail): UserPermisoInput[] {
  const bySeccion = new Map(user.permisos.map((permiso) => [permiso.seccion, permiso]));

  return USER_SECCION_OPTIONS.map((option) => {
    const existing = bySeccion.get(option.seccion);

    return {
      seccion: option.seccion,
      seccion_label: option.label,
      view: existing?.view ?? false,
      edit: existing?.edit ?? false,
      delete: existing?.delete ?? false,
    };
  });
}

export function userPermisosToPayload(rows: UserPermisoInput[]): UserPermisosPayload {
  return {
    permisos: rows.map((row) => ({
      seccion: row.seccion,
      view: row.view,
      edit: row.edit,
      delete: row.delete,
    })),
  };
}
